import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  PickersDayProps,
  PickersCalendarHeaderProps,
} from "@mui/x-date-pickers";
import { DayContain, DateInfoWrapper, DayWrapper, IconExpand } from "./style";
import { ReactComponent as ArrowRight } from "assets/svg/chevron_right.svg";
import { DayTypeValue, IApproveStatus } from "./const";
import DayLeaveType, { DayLeaveTypeProps } from "./components/DayLeaveType";
import { TimeChecking, TimeCheckingProps } from "./components/TimeChecking";
import {
  LackOfTimeChecking,
  LackOfTimeCheckingProps,
} from "./components/LackOfTimeChecking";
import AddLeave, { AddLeaveProps } from "./components/AddLeave";
import { useDialog } from "helper/customHook";
import { DIALOG } from "helper/enum/dialogEnum";
import ArrivingLateLeavingEarly, {
  ArrivingLateLeavingEarlyProps,
} from "./components/ArrivingLateLeavingEarly";
import { useDispatch, useSelector } from "react-redux";
import { updateAttendanceDetail } from "modules/DeclareLeave/redux/attendanceDetail";
import moment from "moment";
import {
  selectArriveLateDetailData,
  selectFormReqArrivingLateLeaveEarly,
  selectFormReqLeave,
  selectRequestLeaveDetailData,
} from "modules/DeclareLeave/redux/selector";
import { DayLeaveType as dayType } from "core/http/service/decleareLeave/const";

import CloseIcon from "@mui/icons-material/Close";
import { isOpenDialogSelector } from "redux-store/static/redux/selector";
import {
  formReqUpdateDetailFormArriveLate,
  TypeSelect,
} from "modules/DeclareLeave/redux/formReqArrivingLateLeaveEarly";
import {
  calculateTotalLeaveDay,
  formReqUpdateDetail,
  updateTotalLeaveDay,
} from "modules/DeclareLeave/redux/formReqLeave";
import {
  getLeaveManagerReqStart,
  updateFormReqLeaveManager,
} from "modules/DeclareLeave/redux/getLeaveManagerReq";
import { MissData, MissDataProps } from "./components/MissData";

export interface HighlightDay {
  type?: string;
  payload:
    | {
        checkInTime?: string;
        checkOutTime?: string;
        earlyLeaveDuration?: string;
        earlyLeaveRequest?: string;
        lateDuration?: string;
        lateRequest?: string;
      }
    | Array<{
        leaveType: string; // TODO: Define leave type
        leaveCategory: string; //TODO: Define leave category
        leaveCategoryDescription: string;
        status: IApproveStatus;
      }>;
}

const DayContent = {
  dayLeave: (payload: DayLeaveTypeProps[]) => (
    <DayLeaveType listLeave={payload} />
  ),
  timeChecking: (payload: TimeCheckingProps) => <TimeChecking {...payload} />,
  lackOfTimeChecking: (payload: LackOfTimeCheckingProps) => (
    <LackOfTimeChecking {...payload} />
  ),

  addLeave: (payload: AddLeaveProps) => <AddLeave {...payload} />,
  arrivingLateLeavingEarly: (payload: ArrivingLateLeavingEarlyProps) => (
    <ArrivingLateLeavingEarly {...payload} />
  ),
  noData: (payload: MissDataProps) => <MissData {...payload} />,
};

interface ServerDayProps
  extends Partial<PickersDayProps<any> & PickersCalendarHeaderProps<any>> {
  highlightedDays?: { [key: string]: HighlightDay[] };
  isDisableDetail?: boolean;
}

const isDayAfterToday = (day: moment.Moment) => day.isAfter(moment(), "day");

const ServerDay: React.FC<ServerDayProps> = (props) => {
  const {
    highlightedDays = {},
    day,
    outsideCurrentMonth,
    onDaySelect,
    isFirstVisibleCell,
    isLastVisibleCell,
    isDisableDetail,
    ...other
  } = props;

  const dispatch = useDispatch();
  const viewDayDetailDialog = useDialog(DIALOG.VIEW_DAY_DETAIL);
  const dataDay = highlightedDays[day.format("DD/MM/YYYY")];
  const openArriveLate = useSelector(
    isOpenDialogSelector(DIALOG.REQUEST_ARRIVING_LATE_LEAVING_EARLY)
  );
  const openLeave = useSelector(isOpenDialogSelector(DIALOG.REQUEST_LEAVE));
  // dữ liệu đki nghỉ phép
  const { data: dataLeave } = useSelector(selectFormReqLeave);
  const { details: dataFormLeave } = dataLeave;
  const dataDetailLeave = useSelector(selectRequestLeaveDetailData);

  // dữ liệu đki đi muộn về sớm
  const { data: dataArriveLate } = useSelector(
    selectFormReqArrivingLateLeaveEarly
  );

  const { details: dataFormArriveLate } = dataArriveLate;
  const dataDetailArriveLate = useSelector(selectArriveLateDetailData);

  const listData: any[] = useMemo(() => {
    if (!openArriveLate && !openLeave) return [];
    return openArriveLate ? dataFormArriveLate : dataFormLeave;
  }, [openArriveLate, openLeave, dataFormArriveLate, dataFormLeave]);

  const dataDetail = useMemo(() => {
    if (!openArriveLate && !openLeave) return {};
    return openArriveLate ? dataDetailArriveLate : dataDetailLeave;
  }, [dataDetailArriveLate, dataDetailLeave, openLeave, openArriveLate]);

  const isIncludesData = useMemo(() => {
    if (!openArriveLate && !openLeave) return false;
    if (
      dataArriveLate?.type === TypeSelect.LONG_TERM ||
      dataLeave?.type === TypeSelect.LONG_TERM
    )
      return false;
    if (dataDetail && dataDetail?.details) {
      return dataDetail?.details.some(
        (i) => moment(i?.fromDate).toISOString() === day.toISOString()
      );
    }
    if (listData?.length > 0) {
      return listData
        ?.filter((it) => !!it.isNew && it?.reasonCode)
        ?.some((i) => i?.fromDate.toISOString() === day.toISOString());
    }
    return false;
  }, [
    dataDetail,
    listData,
    day,
    openArriveLate,
    openLeave,
    dataArriveLate,
    dataLeave,
  ]);

  const handleClear = () => {
    const dateDataIdx = listData.reduce((acc, value, index) => {
      if (
        dataDetail?.details &&
        dataDetail?.details?.some((item) => item?.id === value?.id) &&
        value?.fromDate.toISOString() === day.toISOString()
      ) {
        acc.push(index);
      }
      if (value?.fromDate.toISOString() === day.toISOString() && value?.isNew) {
        acc.push(index);
      }
      return acc;
    }, []);

    const newData = listData?.map((it, idx) => {
      if (dateDataIdx?.includes(idx)) {
        let tempData = { ...it };
        if (openLeave) {
          tempData = {
            ...tempData,
            reasonCode: "",
            reasonName: "",
            leaveReasonTypeName: "",
            leaveReasonType: "",
            requireAttachFiles: false,
            type: undefined,
            isNew: false,
            files: [],
            files_files: [],
          };
        } else {
          tempData = {
            ...tempData,
            reasonCode: "",
            minuteAmount: "",
            reasonName: "",
            isNew: true,
          };
        }
        return tempData;
      }
      return it;
    });

    if (openArriveLate) {
      dispatch(formReqUpdateDetailFormArriveLate(newData));
    } else {
      const listData = newData.filter((detail) => {
        if (dataDetail?.id) {
          return (
            dataDetail?.details?.some(
              (it) =>
                moment(it?.fromDate)?.toISOString() ===
                detail?.fromDate?.toISOString()
            ) && detail?.reasonCode
          );
        }
        return detail?.isNew && detail?.reasonCode;
      });
      const totalLeaveDay = calculateTotalLeaveDay(listData);
      dispatch(updateFormReqLeaveManager(listData));

      dispatch(updateTotalLeaveDay(totalLeaveDay));

      dispatch(formReqUpdateDetail(newData));
      dispatch(getLeaveManagerReqStart());

      // dispatch(resetListLeaveDetail());
    }
  };

  const isHoliday =
    Array.isArray(dataDay) &&
    dataDay.some(
      (i) =>
        i.type === DayTypeValue.DAY_LEAVE &&
        Array.isArray(i.payload) &&
        i.payload.some((j) => j.leaveCategory === dayType.HOLIDAY)
    );

  const viewDetail = () => {
    const isNextLeave =
      Array.isArray(dataDay) &&
      dataDay.some((i) => i.type === DayTypeValue.DAY_LEAVE && i.payload);

    const isNextArriveLate =
      Array.isArray(dataDay) &&
      dataDay.some(
        (i) =>
          (i.type === DayTypeValue.LACK_OF_TIME_CHECKING &&
            (i.payload as any)?.lateRequest) ||
          (i.payload as any)?.earlyLeaveRequest
      );

    if (
      isDisableDetail ||
      isHoliday ||
      !dataDay ||
      (isDayAfterToday(day) && !isNextLeave && !isNextArriveLate)
    )
      return;

    dispatch(
      updateAttendanceDetail({ data: dataDay, date: day.format("DD/MM/YYYY") })
    );
    viewDayDetailDialog.open();
  };

  const handleOnClickItemInfo = (type: string) => {
    if (type !== "noData") {
      viewDetail();
    }
  };

  const renderContent = () => {
    return (
      <>
        <IconExpand>
          {!openLeave && !openArriveLate && <ArrowRight onClick={viewDetail} />}
        </IconExpand>

        {(dataDay ?? []).map((data) => {
          const { type, payload } = data;
          return (
            <Box
              onClick={() => {
                handleOnClickItemInfo(type);
              }}
            >
              {DayContent[type](payload)}
            </Box>
          );
        })}
      </>
    );
  };

  return (
    <DayContain
      {...other}
      outsideCurrentMonth={outsideCurrentMonth}
      day={day}
      onDaySelect={outsideCurrentMonth ? () => {} : onDaySelect}
      isFirstVisibleCell={isFirstVisibleCell}
      isLastVisibleCell={isLastVisibleCell}
    >
      <DayWrapper>
        <DateInfoWrapper>
          <Typography fontSize={18} fontWeight={600} className="day">
            {day.format("D")}
          </Typography>
          {dataArriveLate?.type === TypeSelect.LONG_TERM ||
          dataLeave?.type === TypeSelect.LONG_TERM
            ? null
            : isIncludesData && (
                <CloseIcon sx={{ cursor: "pointer" }} onClick={handleClear} />
              )}
        </DateInfoWrapper>
        {renderContent()}
      </DayWrapper>
    </DayContain>
  );
};

export default ServerDay;
